/**
 * PortalFooter - Shared footer component for portal pages
 *
 * Displays copyright, Terms / Privacy / Changelog links, and Portal version.
 * Styling uses brand tokens from the UI Style Guide (no hard-coded hex).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { PORTAL_UI_VERSION } from '@/constants/portal';
import { navigatePortalInternal } from './PortalLayout.store';

export interface PortalFooterProps {
  /** Portal version override (e.g., "v3"). Defaults to PORTAL_UI_VERSION constant. */
  version?: string;
  /** Portal build number (e.g., "6.8") */
  buildNumber?: string;
}

export const PortalFooter = React.memo<PortalFooterProps>(({ version, buildNumber }) => {
  const { t } = useTranslation(NS.COMMON);
  const displayVersion = version || PORTAL_UI_VERSION;

  const handleInternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    const href = event.currentTarget.getAttribute('href');
    if (!href || !href.startsWith('/')) return;
    event.preventDefault();
    navigatePortalInternal(href);
  };

  return (
    <div>
      {/* Compact footer: mobile + tablet */}
      <div className="block lg:hidden text-center py-3">
        <p className="text-[10px] text-brand-body-secondary">
          {t('Footer_Copyright', '© 2015-2026 Loren Data Corp.')}
          {' · '}{displayVersion}
        </p>
      </div>

      {/* Full footer: desktop (lg+) */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4 py-6">
          <img
            src="/img/lorenData_Logo1x.png"
            alt="Loren Data - Powered by Innovation"
            className="h-6 w-auto flex-shrink-0"
          />
          <div className="flex-1 text-[10px] text-brand-body-secondary leading-relaxed text-center">
            <p>
              {t('Footer_Copyright', '© 2015-2026 Loren Data Corp. All Rights Reserved.')}{' '}
              <a
                href="/terms"
                onClick={handleInternalLinkClick}
                className="underline hover:text-brand-body-primary transition-colors"
              >
                {t('Footer_TermsOfService', 'Terms of Service')}
              </a>{' '}
              &amp;{' '}
              <a
                href="https://www.ld.com/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-brand-body-primary transition-colors"
              >
                {t('Footer_PrivacyPolicy', 'Privacy Policy')}
              </a>
            </p>
            <p>
              {t('Footer_PortalPoweredBy', 'Powered by ECGridOS v4.1 (Build 485), Portal Version: {{version}} Build: {{buildNumber}} -', {
                version: displayVersion,
                buildNumber: buildNumber || '6.8',
              })}{' '}
              <a
                href="/change-log"
                onClick={handleInternalLinkClick}
                className="underline hover:text-brand-body-primary transition-colors"
              >
                {t('Footer_ViewChangeLog', 'View Change Log')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

PortalFooter.displayName = 'PortalFooter';
